import Layout from "@/components/Layout";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import { Link } from "react-router-dom";

const exhibitions = [
  {
    code: "Chapter 1",
    title: "Chapter 1",
    venue: "The Crypt Gallery · London",
    dates: "21 — 23 November 2024",
    image: "/posters/chapter-1.jpg",
    body: [
      "The first public exhibition of Nick Fraser's work, held in the crypt beneath St Pancras Old Church.",
      "Curated by Marcus O'Shaughnessy with Mark O'Shaughnessy. Atmospheric live music, candlelit, four nights — a posthumous unveiling of an artist whose work had remained almost entirely unseen.",
    ],
    status: "Closed",
    photos: [
      { src: "/crypt/crypt-1.jpg", caption: "The Crypt Gallery, beneath St Pancras Old Church." },
      { src: "/crypt/crypt-2.jpg", caption: "Nick Fraser — The Crypt." },
      { src: "/crypt/crypt-3.jpg", caption: "The Crypt, London, NW1." },
      { src: "/crypt/crypt-marcus.jpg", caption: "Marcus O'Shaughnessy at Chapter 1." },
    ],
  },
  {
    code: "Chapter 2",
    title: "Chapter 2 — A continuation",
    venue: "Shoreditch Arts Club · London",
    venueUrl: "https://shoreditchartsclub.com",
    dates: "31 July 2026 · one day only",
    image: "/posters/chapter-2.jpg",
    body: [
      "Chapter 2 continues the story of Nick Fraser through selected works, archive material, limited-edition prints and a deeper look at the private world behind the paintings.",
      "This one-day exhibition will explore themes of identity, masculinity, memory and the unspoken life within the work.",
    ],
    status: "Forthcoming",
    cta: { label: "Enter the Release", to: "/chapter-ii" },
  },
];

export default function Exhibitions() {
  return (
    <Layout>
      <main data-testid="exhibitions-page" className="bg-[color:var(--bone)]">
        <PageHeader
          eyebrow="Exhibitions"
          title="Released in chapters."
          lede="Each chapter is a deliberate, narrow window onto Nick's archive — a room, a few nights, a body of work allowed to breathe."
          image="/artworks/light-language.jpg"
        />

        {exhibitions.map((ex, i) => (
          <section
            key={ex.code}
            className={`${i % 2 === 0 ? "bone-section" : "dark-section"} py-32 md:py-48`}
            data-testid={`exhibition-${ex.code.replace(/\s/g, "-").toLowerCase()}`}
          >
            <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-start">
              <Reveal className="md:col-span-6" slow>
                <figure>
                  <img
                    src={ex.image}
                    alt={ex.title}
                    className="w-full h-auto"
                  />
                </figure>
              </Reveal>

              <div className="md:col-span-6 md:pt-12">
                <Reveal>
                  <p className="hairline opacity-60 mb-3">{ex.code}</p>
                  <h2 className="font-serif italic text-4xl md:text-6xl leading-[1.0]">
                    {ex.title}
                  </h2>
                  <p className="hairline opacity-60 mt-6">
                    {ex.venue} · {ex.dates}
                  </p>
                  {ex.venueUrl && (
                    <a
                      href={ex.venueUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="link-quiet hairline mt-2 inline-block"
                      data-testid={`exhibition-venue-link-${i}`}
                    >
                      Visit the venue →
                    </a>
                  )}
                  <p
                    className={`hairline mt-3 ${
                      ex.status === "Forthcoming"
                        ? "text-[color:var(--ochre)]"
                        : "opacity-50"
                    }`}
                    data-testid={`exhibition-status-${i}`}
                  >
                    {ex.status}
                  </p>
                </Reveal>

                <Reveal delay={140} className="mt-10 space-y-6">
                  {ex.body.map((p, j) => (
                    <p
                      key={j}
                      className="font-serif italic text-xl md:text-2xl leading-[1.4] opacity-85 max-w-[55ch]"
                    >
                      {p}
                    </p>
                  ))}
                </Reveal>

                {ex.cta && (
                  <Reveal delay={220} className="mt-12">
                    <Link
                      to={ex.cta.to}
                      className="link-quiet font-serif italic text-xl md:text-2xl"
                      data-testid={`exhibition-cta-${i}`}
                    >
                      {ex.cta.label} →
                    </Link>
                  </Reveal>
                )}
              </div>
            </div>

            {/* Optional photo strip */}
            {ex.photos && ex.photos.length > 0 && (
              <div
                className="max-w-[1500px] mx-auto px-6 md:px-12 mt-24 md:mt-32"
                data-testid={`exhibition-photos-${i}`}
              >
                <Reveal>
                  <p className="hairline opacity-55 mb-10">
                    From the exhibition
                  </p>
                </Reveal>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
                  {ex.photos.map((p, j) => (
                    <Reveal key={p.src} delay={j * 90} slow>
                      <figure data-testid={`exhibition-photo-${i}-${j}`}>
                        <img
                          src={p.src}
                          alt={p.caption}
                          loading="lazy"
                          className="w-full h-auto block aspect-[2/3] object-cover"
                        />
                        <figcaption className="hairline opacity-55 mt-3">
                          {p.caption}
                        </figcaption>
                      </figure>
                    </Reveal>
                  ))}
                </div>
              </div>
            )}
          </section>
        ))}
      </main>
    </Layout>
  );
}
